import { feedbackTypeOfProposal, type Feedback, type FeedbackProposal } from '@cockpit/shared';
import { defineStore } from 'pinia';
import { reactive, ref, shallowRef, watch } from 'vue';
import { emptyFeedbackDraft, type FeedbackDraft } from '../feedback';
import { useAssistantStore } from './assistant';

/**
 * Lot AQ : panneau « Faire un retour » (maquette retours-1410, onglet A), ouvert depuis la bulle, le bouton
 * « Signaler un problème » de l'en-tête ou une proposition de l'assistant. Un seul panneau à la fois : ouvrir l'un
 * ferme l'autre, et le brouillon de chacun reste en mémoire.
 */
export const useFeedbackPanelStore = defineStore('feedbackPanel', () => {
  const assistant = useAssistantStore();
  const open = ref(false);
  const draft = reactive<FeedbackDraft>(emptyFeedbackDraft());
  /** « Où ? » changé à la main (ou fixé par l'assistant) : il ne suit plus l'écran affiché. */
  const screenChosen = ref(false);
  /** Retour envoyé depuis le panneau : la confirmation s'affiche jusqu'à la fermeture. */
  const sent = shallowRef<Feedback | null>(null);
  /** Lot AW : retour fait sans réseau, gardé sur le téléphone (envoyé au retour du réseau). */
  const queued = ref(false);
  /** Dernier retour envoyé depuis le panneau (la page Retours, si elle est ouverte, l'ajoute à sa liste). */
  const lastSent = shallowRef<Feedback | null>(null);

  function show(): void {
    assistant.hide();
    open.value = true;
  }

  function hide(): void {
    open.value = false;
    sent.value = null;
  }

  /** « Annuler » : le brouillon est abandonné et le panneau se ferme. */
  function discard(): void {
    Object.assign(draft, emptyFeedbackDraft(draft.screen));
    screenChosen.value = false;
    hide();
  }

  /** Lot AD : retour proposé par l'assistant : le panneau s'ouvre prérempli, la conversation jointe. */
  function prefillFromAssistant(proposal: FeedbackProposal, conversationId: string | null): void {
    draft.type = feedbackTypeOfProposal(proposal.type);
    draft.screen = proposal.screen;
    draft.text = proposal.text;
    draft.observed = proposal.observed;
    draft.conversationId = conversationId;
    screenChosen.value = true;
    sent.value = null;
    show();
  }

  function markSent(feedback: Feedback, wasQueued = false): void {
    Object.assign(draft, emptyFeedbackDraft(draft.screen));
    screenChosen.value = false;
    queued.value = wasQueued;
    sent.value = feedback;
    lastSent.value = feedback;
  }

  // L'assistant s'ouvre (bulle, « Comment c'est calculé ? »…) : le panneau de retour se ferme.
  watch(
    () => assistant.open,
    (assistantOpen) => {
      if (assistantOpen) hide();
    },
  );

  return {
    open,
    draft,
    screenChosen,
    sent,
    queued,
    lastSent,
    show,
    hide,
    discard,
    prefillFromAssistant,
    markSent,
  };
});
